import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquareHeart, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import Roulette from "./Roulette";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = { name: "", phone: "", message: "" };
const SENT_KEY = "troy_fb_sent";

const loadSaved = () => {
  try {
    const raw = sessionStorage.getItem(SENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export default function Feedback() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [player, setPlayer] = useState(loadSaved);
  const [sent, setSent] = useState(() => !!loadSaved());

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.trim();
    const message = form.message.trim();
    if (!name || !phone || !message) {
      toast.error("Preencha todos os campos: nome, telefone e mensagem.");
      return;
    }
    if (name.length < 2) {
      toast.error("Digite seu nome completo (mínimo 2 letras).");
      return;
    }
    if (phone.replace(/\D/g, "").length < 8) {
      toast.error("Telefone inválido. Inclua DDD + número.");
      return;
    }
    if (message.length < 3) {
      toast.error("Escreva uma mensagem um pouco maior (mínimo 3 letras).");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/feedbacks`, { name, phone, message });
      const p = { phone, name };
      try {
        sessionStorage.setItem(SENT_KEY, JSON.stringify(p));
      } catch {}
      setPlayer(p);
      setSent(true);
      setForm(INITIAL);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (typeof detail === "string") {
        toast.error(detail);
      } else if (Array.isArray(detail)) {
        const fieldNames = { name: "nome", phone: "telefone", message: "mensagem" };
        const field = fieldNames[detail[0]?.loc?.[1]] || "campo";
        const isLong = detail[0]?.type === "string_too_long";
        toast.error(
          isLong
            ? `O campo ${field} está muito longo. Diminua um pouco e tente de novo.`
            : `Verifique o campo ${field} e tente de novo.`
        );
      } else {
        toast.error("Sem conexão no momento. Verifique sua internet e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="feedback"
      data-testid="feedback-section"
      className="relative py-24 md:py-32 bg-[#08080A] border-t border-white/5"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_50%,rgba(255,204,0,0.08),transparent_50%)]" />

      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.25em] uppercase text-[#FFCC00] font-bold mb-3">
            Fale com a gente
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mb-5 text-balance">
            Sua opinião é muito <span className="text-[#FFCC00]">importante</span> para nós!
          </h2>
          <p className="text-white/70 text-lg leading-relaxed max-w-xl mx-auto">
            Envie sua sugestão, elogio, reclamação ou ideia e nos ajude a
            melhorar nossos serviços.
          </p>
        </div>

        {sent ? (
          <div
            key="fb-success"
            data-testid="feedback-success-message"
            className="rounded-3xl bg-[#121215] border border-[#FFCC00]/15 p-10 md:p-14 text-center"
          >
            <CheckCircle2 className="w-12 h-12 text-[#25D366] mx-auto mb-3" />
            <h3 className="font-display text-2xl md:text-3xl font-black mb-2">
              🎉 FEEDBACK ENVIADO!
            </h3>
            <p className="text-[#FFCC00] font-bold text-lg mb-6">
              "Agora sua participação na Roleta Troy está liberada!"
            </p>
            {player ? <Roulette phone={player.phone} name={player.name} /> : null}
            <button
              type="button"
              data-testid="feedback-send-another-btn"
              onClick={() => {
                try {
                  sessionStorage.removeItem(SENT_KEY);
                } catch {}
                setPlayer(null);
                setSent(false);
              }}
              className="mt-6 text-sm text-white/50 hover:text-white underline underline-offset-4"
            >
              Enviar outro feedback
            </button>
          </div>
        ) : (
          <form
            key="fb-form"
            onSubmit={onSubmit}
            noValidate
            data-testid="feedback-form"
            className="card-accent-top relative rounded-3xl bg-[#121215] border border-[#FFCC00]/15 p-7 md:p-10"
          >
            <div className="absolute -inset-1 bg-[#FF3B30]/5 blur-3xl rounded-3xl -z-10" />
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="fb-name" className="text-white/80 text-sm font-semibold mb-2 block">
                    Nome *
                  </Label>
                  <Input
                    id="fb-name"
                    name="name"
                    data-testid="feedback-field-name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Seu nome"
                    className="bg-[#1C1C22] border-white/10 h-12 rounded-xl text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                  />
                </div>
                <div>
                  <Label htmlFor="fb-phone" className="text-white/80 text-sm font-semibold mb-2 block">
                    Telefone *
                  </Label>
                  <Input
                    id="fb-phone"
                    name="phone"
                    data-testid="feedback-field-phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="(31) 9 9999-9999"
                    className="bg-[#1C1C22] border-white/10 h-12 rounded-xl text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="fb-message" className="text-white/80 text-sm font-semibold mb-2 block">
                  Mensagem *
                </Label>
                <Textarea
                  id="fb-message"
                  name="message"
                  data-testid="feedback-field-message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Escreva sua sugestão, elogio, reclamação ou ideia..."
                  rows={5}
                  className="bg-[#1C1C22] border-white/10 rounded-xl text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="feedback-submit-btn"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-bold py-4 px-8 rounded-xl hover:bg-[#E6B800] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_0_24px_rgba(255,204,0,0.3)]"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquareHeart className="w-5 h-5" />}
              {loading ? "Enviando..." : "Enviar Feedback"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
